import { useEffect } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { apiFetch, uploadPhoto } from "./api.js";
import {
  drainBulkQueue,
  drainFishingDayQueue,
  drainQueue,
  pendingPhotoToBlob,
} from "./offlineQueue.js";

/**
 * Drains both offline queues (single catch + bulk småfisk) whenever the app
 * comes back online, and once on mount. Mounted once at the app shell level
 * (Layout) rather than inside CatchFormPage/BulkSmåfiskPage individually —
 * previously a queued submission only drained if the user happened to still
 * be on that exact page when connectivity returned, so a bulk batch queued
 * offline and then left behind (e.g. navigating to check stats) could sit
 * undrained indefinitely even on a good connection.
 *
 * Also drains when the app returns to the foreground. iOS has no background
 * sync and suspends a backgrounded app, so a phone that regained signal in
 * the fisher's pocket never delivers an `online` event to act on.
 */
export function useOfflineSync() {
  const { getToken } = useAuth();

  useEffect(() => {
    function drainAll() {
      drainQueue(
        (body) =>
          apiFetch("/catches", {
            method: "POST",
            body: JSON.stringify(body),
            getToken,
          }),
        (photo) =>
          uploadPhoto(pendingPhotoToBlob(photo), photo.mimeType, getToken),
      );
      drainBulkQueue((body) =>
        apiFetch("/catches/bulk", {
          method: "POST",
          body: JSON.stringify(body),
          getToken,
        }),
      );
      drainFishingDayQueue((body) =>
        apiFetch("/fishing-sessions", {
          method: "POST",
          body: JSON.stringify(body),
          getToken,
        }),
      );
    }
    function drainIfForegrounded() {
      if (document.visibilityState === "visible" && navigator.onLine) {
        drainAll();
      }
    }
    window.addEventListener("online", drainAll);
    document.addEventListener("visibilitychange", drainIfForegrounded);
    drainAll();
    return () => {
      window.removeEventListener("online", drainAll);
      document.removeEventListener("visibilitychange", drainIfForegrounded);
    };
    // getToken's identity changes every render (AuthContext wraps it in a
    // fresh closure each time) but always calls through to the same
    // underlying stable client — capture it once at mount, matching the
    // pattern the per-page effects this replaces already relied on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
